import jsPDF from 'jspdf';
import type { Analysis, RiskFactor, TechnicalSkill } from '../types/models';
import { supabase } from '../lib/supabase';

class ProfessionalPDF {
  private pdf: jsPDF;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number = 40;
  private currentY: number = 40;

  constructor() {
    this.pdf = new jsPDF('p', 'mm', 'a4');
    this.pageWidth = this.pdf.internal.pageSize.getWidth();
    this.pageHeight = this.pdf.internal.pageSize.getHeight();
  }

  private newPage(): void {
    this.pdf.addPage();
    this.currentY = this.margin;
  }

  private checkSpace(needed: number): void {
    if (this.currentY + needed > this.pageHeight - this.margin) {
      this.newPage();
    }
  }

  addHeader(title: string, organizationName?: string): void {
    // Organization name (top right)
    if (organizationName) {
      this.pdf.setFontSize(9);
      this.pdf.setTextColor(120, 120, 120);
      const orgWidth = this.pdf.getTextWidth(organizationName);
      this.pdf.text(organizationName, this.pageWidth - this.margin - orgWidth, 20);
    }

    // Main title
    this.pdf.setFontSize(22);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(30, 41, 59);
    this.pdf.text(title, this.margin, this.currentY);
    this.currentY += 15;

    // Blue line
    this.pdf.setDrawColor(59, 130, 246);
    this.pdf.setLineWidth(1);
    this.pdf.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
    this.currentY += 15;
  }

  addSection(title: string): void {
    this.checkSpace(20);
    this.currentY += 8;
    
    this.pdf.setFontSize(14);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(30, 41, 59);
    this.pdf.text(title, this.margin, this.currentY);
    this.currentY += 10;
  }

  addKeyValuePair(key: string, value: string, bold: boolean = false): void {
    this.checkSpace(10);
    
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(30, 41, 59);
    this.pdf.text(`${key}:`, this.margin, this.currentY);
    
    const keyWidth = this.pdf.getTextWidth(`${key}: `);
    this.pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    
    if (bold) {
      this.pdf.setTextColor(59, 130, 246);
    } else {
      this.pdf.setTextColor(75, 85, 99);
    }
    
    // Wrap long text
    const maxWidth = this.pageWidth - this.margin * 2 - keyWidth - 5;
    const lines = this.pdf.splitTextToSize(value, maxWidth);
    
    for (let i = 0; i < lines.length; i++) {
      if (i > 0) {
        this.checkSpace(6);
        this.pdf.text(lines[i], this.margin + keyWidth + 5, this.currentY);
        this.currentY += 6;
      } else {
        this.pdf.text(lines[i], this.margin + keyWidth + 5, this.currentY);
        this.currentY += 6;
      }
    }
    this.currentY += 2;
  }

  addScoreBar(label: string, score: number, maxScore: number = 10): void {
    this.checkSpace(15);
    
    // Label
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(30, 41, 59);
    this.pdf.text(label, this.margin, this.currentY);
    this.currentY += 8;
    
    // Score bar
    const barWidth = 80;
    const barHeight = 4;
    const percentage = (score / maxScore);
    
    // Background
    this.pdf.setFillColor(229, 231, 235);
    this.pdf.rect(this.margin, this.currentY - 2, barWidth, barHeight, 'F');
    
    // Fill
    if (percentage >= 0.8) this.pdf.setFillColor(34, 197, 94);  // green
    else if (percentage >= 0.6) this.pdf.setFillColor(245, 158, 11); // amber
    else this.pdf.setFillColor(239, 68, 68); // red
    
    this.pdf.rect(this.margin, this.currentY - 2, barWidth * percentage, barHeight, 'F');
    
    // Score text
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(75, 85, 99);
    this.pdf.text(`${score}/${maxScore} (${Math.round(percentage * 100)}%)`, this.margin + barWidth + 5, this.currentY);
    
    this.currentY += 10;
  }

  addBulletPoints(items: string[]): void {
    if (!items || items.length === 0) {
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(156, 163, 175);
      this.pdf.text('None specified', this.margin + 5, this.currentY);
      this.currentY += 8;
      return;
    }

    items.forEach(item => {
      this.checkSpace(8);
      
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(30, 41, 59);
      this.pdf.text('•', this.margin, this.currentY);
      
      const maxWidth = this.pageWidth - this.margin * 2 - 8;
      const lines = this.pdf.splitTextToSize(item, maxWidth);
      
      for (let i = 0; i < lines.length; i++) {
        if (i > 0) this.checkSpace(6);
        this.pdf.text(lines[i], this.margin + 8, this.currentY);
        this.currentY += 6;
      }
      this.currentY += 2;
    });
  }

  addTechnicalSkills(skills: TechnicalSkill[]): void {
    if (!skills || skills.length === 0) {
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(156, 163, 175);
      this.pdf.text('No technical skills assessed', this.margin, this.currentY);
      this.currentY += 8;
      return;
    }

    skills.forEach(skill => {
      this.checkSpace(20);
      
      // Skill name
      this.pdf.setFontSize(11);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(30, 41, 59);
      this.pdf.text(skill.skill, this.margin, this.currentY);
      
      // Proficiency level (with proper spacing)
      this.pdf.setFontSize(9);
      let badgeColor: [number, number, number] = [156, 163, 175];
      if (skill.proficiency === 'EXPERT') badgeColor = [34, 197, 94];
      else if (skill.proficiency === 'ADVANCED') badgeColor = [59, 130, 246];
      else if (skill.proficiency === 'INTERMEDIATE') badgeColor = [245, 158, 11];
      
      this.pdf.setTextColor(badgeColor[0], badgeColor[1], badgeColor[2]);
      const skillWidth = this.pdf.getTextWidth(skill.skill);
      this.pdf.text(skill.proficiency, this.margin + skillWidth + 15, this.currentY);
      
      this.currentY += 10;
      
      // Evidence (with proper indentation)
      if (skill.evidence) {
        this.pdf.setFontSize(9);
        this.pdf.setTextColor(107, 114, 128);
        const evidenceText = `Evidence: ${skill.evidence}`;
        const maxWidth = this.pageWidth - this.margin * 2 - 10;
        const lines = this.pdf.splitTextToSize(evidenceText, maxWidth);
        
        for (let i = 0; i < lines.length; i++) {
          this.checkSpace(5);
          this.pdf.text(lines[i], this.margin + 5, this.currentY);
          this.currentY += 5;
        }
        this.currentY += 3;
      }
      
      this.currentY += 5;
    });
  }

  addRiskFactors(risks: RiskFactor[]): void {
    if (!risks || risks.length === 0) {
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(34, 197, 94);
      this.pdf.text('✓ No significant risks identified', this.margin, this.currentY);
      this.currentY += 8;
      return;
    }

    risks.forEach(risk => {
      this.checkSpace(20);
      
      // Risk level indicator
      this.pdf.setFontSize(9);
      this.pdf.setFont('helvetica', 'bold');
      
      let color: [number, number, number] = [156, 163, 175];
      if (risk.severity === 'HIGH') color = [239, 68, 68];
      else if (risk.severity === 'MEDIUM') color = [245, 158, 11];
      else if (risk.severity === 'LOW') color = [34, 197, 94];
      
      this.pdf.setTextColor(color[0], color[1], color[2]);
      this.pdf.text(`${risk.severity} RISK`, this.margin, this.currentY);
      this.currentY += 8;
      
      // Description
      this.pdf.setFontSize(10);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setTextColor(30, 41, 59);
      
      const maxWidth = this.pageWidth - this.margin * 2 - 5;
      const descLines = this.pdf.splitTextToSize(risk.description, maxWidth);
      
      for (let i = 0; i < descLines.length; i++) {
        this.checkSpace(6);
        this.pdf.text(descLines[i], this.margin + 5, this.currentY);
        this.currentY += 6;
      }
      
      // Mitigation
      if (risk.mitigation) {
        this.currentY += 2;
        this.pdf.setFontSize(9);
        this.pdf.setTextColor(107, 114, 128);
        const mitigationText = `Mitigation: ${risk.mitigation}`;
        const mitigationLines = this.pdf.splitTextToSize(mitigationText, maxWidth);
        
        for (let i = 0; i < mitigationLines.length; i++) {
          this.checkSpace(5);
          this.pdf.text(mitigationLines[i], this.margin + 5, this.currentY);
          this.currentY += 5;
        }
      }
      
      this.currentY += 8;
    });
  }

  addGrowthPotential(growth: { areas: string[]; recommendations: string[] }): void {
    if (growth.areas?.length) {
      this.pdf.setFontSize(11);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(30, 41, 59);
      this.pdf.text('Growth Areas:', this.margin, this.currentY);
      this.currentY += 8;
      this.addBulletPoints(growth.areas);
      this.currentY += 5;
    }

    if (growth.recommendations?.length) {
      this.pdf.setFontSize(11);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(30, 41, 59);
      this.pdf.text('Recommendations:', this.margin, this.currentY);
      this.currentY += 8;
      this.addBulletPoints(growth.recommendations);
    }
  }

  addLongText(content: string): void {
    if (!content || content.trim() === '') return;
    
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(75, 85, 99);
    
    const maxWidth = this.pageWidth - this.margin * 2;
    const lines = this.pdf.splitTextToSize(content, maxWidth);
    
    for (let i = 0; i < lines.length; i++) {
      this.checkSpace(6);
      this.pdf.text(lines[i], this.margin, this.currentY);
      this.currentY += 6;
    }
    this.currentY += 5;
  }

  addQuestionWithOptions(question: any, index: number, showAnswers: boolean = false): void {
    this.checkSpace(20);
    
    // Question number and text
    this.pdf.setFontSize(11);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(30, 41, 59);
    this.pdf.text(`Question ${index + 1}:`, this.margin, this.currentY);
    this.currentY += 8;
    
    // Question text
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(75, 85, 99);
    const questionText = question.question || question.text || question;
    const maxWidth = this.pageWidth - this.margin * 2;
    const questionLines = this.pdf.splitTextToSize(questionText, maxWidth);
    
    for (let i = 0; i < questionLines.length; i++) {
      this.checkSpace(6);
      this.pdf.text(questionLines[i], this.margin, this.currentY);
      this.currentY += 6;
    }
    this.currentY += 5;
    
    // Multiple choice options (if available)
    if (question.options && Array.isArray(question.options)) {
      question.options.forEach((option: any, optionIndex: number) => {
        this.checkSpace(6);
        this.pdf.setFontSize(9);
        
        // Highlight correct answer if showing answers
        const isCorrect = showAnswers && (
          question.correct === option ||
          question.correct === String.fromCharCode(65 + optionIndex) ||
          (Array.isArray(question.correct) && question.correct.includes(option))
        );
        
        if (isCorrect) {
          this.pdf.setTextColor(34, 197, 94); // Green for correct answer
          this.pdf.setFont('helvetica', 'bold');
        } else {
          this.pdf.setTextColor(107, 114, 128);
          this.pdf.setFont('helvetica', 'normal');
        }
        
        const optionLabel = String.fromCharCode(65 + optionIndex); // A, B, C, D
        const optionText = typeof option === 'string' ? option : (option.text || option.label || option);
        const prefix = isCorrect ? `${optionLabel}. ✓ ` : `${optionLabel}. `;
        
        const optionLines = this.pdf.splitTextToSize(`${prefix}${optionText}`, maxWidth - 10);
        for (let i = 0; i < optionLines.length; i++) {
          this.checkSpace(5);
          this.pdf.text(optionLines[i], this.margin + 10, this.currentY);
          this.currentY += 5;
        }
        this.currentY += 2;
      });
    }
    
    // Code template (for coding questions) - only show if not showing answers
    if (question.template && !showAnswers) {
      this.currentY += 3;
      this.pdf.setFontSize(9);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(139, 69, 19);
      this.pdf.text('Code Template:', this.margin + 5, this.currentY);
      this.currentY += 6;
      
      this.pdf.setFontSize(8);
      this.pdf.setFont('courier', 'normal');
      this.pdf.setTextColor(75, 85, 99);
      const codeLines = this.pdf.splitTextToSize(question.template, maxWidth - 10);
      
      codeLines.forEach((line: string) => {
        this.checkSpace(4);
        this.pdf.text(line, this.margin + 10, this.currentY);
        this.currentY += 4;
      });
      this.currentY += 5;
    }
    
    // Answer details (only when showing answers)
    if (showAnswers) {
      this.currentY += 3;
      
      // Points
      this.pdf.setFontSize(9);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(59, 130, 246);
      this.pdf.text(`Points: ${question.points || 10}`, this.margin + 5, this.currentY);
      this.currentY += 6;
      
      // Expected answer for non-multiple choice questions
      if (question.correct && (!question.options || question.options.length === 0)) {
        this.pdf.setFontSize(9);
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(34, 197, 94);
        const answerText = Array.isArray(question.correct) ? question.correct.join(', ') : question.correct;
        const answerLines = this.pdf.splitTextToSize(`Answer: ${answerText}`, maxWidth - 10);
        
        answerLines.forEach((line: string) => {
          this.checkSpace(5);
          this.pdf.text(line, this.margin + 5, this.currentY);
          this.currentY += 5;
        });
        this.currentY += 3;
      }
      
      // Expected output for coding questions
      if (question.expectedOutput) {
        this.pdf.setFontSize(9);
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(139, 69, 19);
        this.pdf.text('Expected Output:', this.margin + 5, this.currentY);
        this.currentY += 6;
        
        this.pdf.setFontSize(8);
        this.pdf.setFont('courier', 'normal');
        this.pdf.setTextColor(75, 85, 99);
        const outputLines = this.pdf.splitTextToSize(question.expectedOutput, maxWidth - 10);
        
        outputLines.forEach((line: string) => {
          this.checkSpace(4);
          this.pdf.text(line, this.margin + 10, this.currentY);
          this.currentY += 4;
        });
        this.currentY += 5;
      }
      
      // Explanation
      if (question.explanation) {
        this.pdf.setFontSize(9);
        this.pdf.setFont('helvetica', 'italic');
        this.pdf.setTextColor(107, 114, 128);
        const explanationLines = this.pdf.splitTextToSize(`Explanation: ${question.explanation}`, maxWidth - 10);
        
        explanationLines.forEach((line: string) => {
          this.checkSpace(4);
          this.pdf.text(line, this.margin + 5, this.currentY);
          this.currentY += 4;
        });
        this.currentY += 5;
      }
    }
    
    // Question type and time limit (show for all)
    this.currentY += 3;
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(156, 163, 175);
    let typeInfo = `Type: ${question.type || 'Multiple Choice'}`;
    if (question.timeLimit) {
      typeInfo += ` | Time Limit: ${question.timeLimit} min`;
    }
    this.pdf.text(typeInfo, this.margin + 5, this.currentY);
    this.currentY += 8;
    
    this.currentY += 8;
  }

  addAnswerKey(questions: any[]): void {
    this.newPage();
    
    // Answer Key Header
    this.pdf.setFontSize(16);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(220, 38, 38); // Red to distinguish
    this.pdf.text('ANSWER KEY - FOR ASSESSOR USE ONLY', this.margin, this.currentY);
    this.currentY += 12;
    
    // Warning box
    this.pdf.setDrawColor(220, 38, 38);
    this.pdf.setLineWidth(1);
    this.pdf.rect(this.margin, this.currentY, this.pageWidth - this.margin * 2, 20);
    
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(220, 38, 38);
    this.pdf.text('⚠️  CONFIDENTIAL - Do not share with candidates', this.margin + 5, this.currentY + 7);
    this.pdf.text('This section contains all correct answers and explanations', this.margin + 5, this.currentY + 14);
    this.currentY += 25;
    
    // Grading instructions
    this.pdf.setFontSize(11);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(30, 41, 59);
    this.pdf.text('Grading Instructions:', this.margin, this.currentY);
    this.currentY += 8;
    
    const instructions = [
      '• Compare candidate answers with the correct answers below',
      '• Award partial credit for partially correct responses where appropriate',
      '• Consider the explanation provided when evaluating understanding',
      '• For coding questions, test the code against expected outputs',
      '• Total possible points: ' + (questions.reduce((sum, q) => sum + (q.points || 10), 0) || 'Calculate based on points below')
    ];
    
    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(75, 85, 99);
    
    instructions.forEach(instruction => {
      this.checkSpace(5);
      this.pdf.text(instruction, this.margin + 5, this.currentY);
      this.currentY += 5;
    });
    this.currentY += 10;
    
    // Quick reference answer key
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(30, 41, 59);
    this.pdf.text('Quick Reference Answer Key:', this.margin, this.currentY);
    this.currentY += 8;
    
    questions.forEach((question, index) => {
      this.checkSpace(6);
      this.pdf.setFontSize(9);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setTextColor(75, 85, 99);
      
      let answerText = 'Open-ended response';
      if (question.options && question.correct) {
        // Multiple choice - show letter and answer
        if (typeof question.correct === 'string') {
          const optionIndex = question.options.findIndex((opt: any) => opt === question.correct);
          if (optionIndex >= 0) {
            answerText = `${String.fromCharCode(65 + optionIndex)} - ${question.correct}`;
          } else {
            answerText = question.correct;
          }
        }
      } else if (question.correct) {
        answerText = Array.isArray(question.correct) ? question.correct.join(', ') : question.correct;
      }
      
      this.pdf.text(`Q${index + 1}: ${answerText} (${question.points || 10} pts)`, this.margin + 5, this.currentY);
      this.currentY += 5;
    });
    
    this.currentY += 10;
    
    // Detailed answer explanations
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(30, 41, 59);
    this.pdf.text('Detailed Answer Explanations:', this.margin, this.currentY);
    this.currentY += 10;
    
    questions.forEach((question, index) => {
      this.addQuestionWithOptions(question, index, true); // Show answers
    });
  }

  addFooter(organizationName?: string): void {
    const footerY = this.pageHeight - 15;
    
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(156, 163, 175);
    
    // Organization
    if (organizationName) {
      this.pdf.text(`© ${new Date().getFullYear()} ${organizationName}`, this.margin, footerY);
    }
    
    // Date (center)
    const date = new Date().toLocaleDateString();
    const dateWidth = this.pdf.getTextWidth(date);
    this.pdf.text(date, (this.pageWidth - dateWidth) / 2, footerY);
    
    // Page number
    const pageNum = this.pdf.getNumberOfPages();
    this.pdf.text(`Page ${pageNum}`, this.pageWidth - this.margin - 15, footerY);
  }

  addInterviewPlan(
    sections: Array<{
      title: string;
      duration: string;
      questions: string[];
      redFlags: string[];
      greenFlags: string[];
    }>
  ): void {
    sections.forEach((section, index) => {
      if (index > 0) this.checkSpace(15);
      
      // Section header
      this.pdf.setFontSize(12);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(59, 130, 246);
      this.pdf.text(`${section.title} (${section.duration})`, this.margin, this.currentY);
      this.currentY += 8;
      
      // Questions
      if (section.questions.length > 0) {
        this.pdf.setFontSize(10);
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(30, 41, 59);
        this.pdf.text('Key Questions:', this.margin, this.currentY);
        this.currentY += 6;
        
        section.questions.forEach((question, qIndex) => {
          this.checkSpace(8);
          this.pdf.setFontSize(9);
          this.pdf.setFont('helvetica', 'normal');
          this.pdf.setTextColor(75, 85, 99);
          
          const questionText = `${qIndex + 1}. ${question}`;
          const maxWidth = this.pageWidth - this.margin * 2 - 5;
          const questionLines = this.pdf.splitTextToSize(questionText, maxWidth);
          
          questionLines.forEach((line: string) => {
            this.checkSpace(5);
            this.pdf.text(line, this.margin + 5, this.currentY);
            this.currentY += 5;
          });
          this.currentY += 2;
        });
        this.currentY += 3;
      }
      
      // Red Flags
      if (section.redFlags.length > 0) {
        this.pdf.setFontSize(10);
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(220, 38, 38);
        this.pdf.text('Watch Out For (Red Flags):', this.margin, this.currentY);
        this.currentY += 6;
        
        section.redFlags.forEach((flag) => {
          this.checkSpace(6);
          this.pdf.setFontSize(9);
          this.pdf.setFont('helvetica', 'normal');
          this.pdf.setTextColor(185, 28, 28);
          
          const maxWidth = this.pageWidth - this.margin * 2 - 10;
          const flagLines = this.pdf.splitTextToSize(`• ${flag}`, maxWidth);
          
          flagLines.forEach((line: string) => {
            this.checkSpace(4);
            this.pdf.text(line, this.margin + 5, this.currentY);
            this.currentY += 4;
          });
        });
        this.currentY += 3;
      }
      
      // Green Flags
      if (section.greenFlags.length > 0) {
        this.pdf.setFontSize(10);
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(34, 197, 94);
        this.pdf.text('Positive Indicators (Green Flags):', this.margin, this.currentY);
        this.currentY += 6;
        
        section.greenFlags.forEach((flag) => {
          this.checkSpace(6);
          this.pdf.setFontSize(9);
          this.pdf.setFont('helvetica', 'normal');
          this.pdf.setTextColor(22, 163, 74);
          
          const maxWidth = this.pageWidth - this.margin * 2 - 10;
          const flagLines = this.pdf.splitTextToSize(`• ${flag}`, maxWidth);
          
          flagLines.forEach((line: string) => {
            this.checkSpace(4);
            this.pdf.text(line, this.margin + 5, this.currentY);
            this.currentY += 4;
          });
        });
        this.currentY += 3;
      }
      
      this.currentY += 8;
    });
  }

  save(filename: string): void {
    this.addFooter();
    this.pdf.save(filename);
  }
}

// Helper functions
async function getUserOrganization() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name, organization_name')
      .eq('user_id', user.id)
      .maybeSingle();

    return { 
      name: profile?.organization_name || profile?.full_name || 'Resume Analysis Platform' 
    };
  } catch (error) {
    console.error('Error fetching organization:', error);
    return { name: 'Resume Analysis Platform' };
  }
}

function parseRiskFactor(factor: any): RiskFactor | null {
  if (!factor) return null;
  
  // Handle string format
  if (typeof factor === 'string') {
    try {
      const parsed = JSON.parse(factor);
      return {
        severity: parsed.severity || 'MEDIUM',
        description: parsed.description || factor,
        mitigation: parsed.mitigation
      };
    } catch {
      return {
        severity: 'MEDIUM',
        description: factor
      };
    }
  }
  
  // Handle object format
  if (typeof factor === 'object' && factor.description) {
    return {
      severity: factor.severity || 'MEDIUM',
      description: factor.description,
      mitigation: factor.mitigation
    };
  }
  
  return null;
}

function getVerdictDisplay(verdict: string): string {
  const verdictMap: Record<string, string> = {
    'STRONG_FIT': 'Strong Fit - Highly Recommended',
    'POTENTIAL_FIT': 'Potential Fit - Consider with Review',
    'NEEDS_CONSIDERATION': 'Needs Consideration - Proceed with Caution',
    'NOT_RECOMMENDED': 'Not Recommended - Significant Gaps'
  };
  
  return verdictMap[verdict] || verdict.replace(/_/g, ' ');
}

// Export functions
export async function exportAnalysisToPDF(
  analysis: Analysis, 
  selectedPerspective?: string,
  transformedSections?: Array<{
    title: string;
    duration: string;
    questions: string[];
    redFlags: string[];
    greenFlags: string[];
  }>
) {
  try {
    const organization = await getUserOrganization();
    
    // Get candidate and job details
    const [candidateResult, jobResult] = await Promise.all([
      supabase.from('candidates').select('name').eq('id', analysis.candidate_id).maybeSingle(),
      supabase.from('jobs').select('title, company').eq('id', analysis.job_id).maybeSingle()
    ]);

    const candidateName = candidateResult.data?.name || `Candidate ${analysis.candidate_id}`;
    const jobTitle = jobResult.data?.title || `Position ${analysis.job_id}`;
    const company = jobResult.data?.company || 'Company';

    const pdf = new ProfessionalPDF();

    // Header
    pdf.addHeader('Candidate Analysis Report', organization?.name);

    // Basic Info
    pdf.addSection('Analysis Overview');
    pdf.addKeyValuePair('Candidate', candidateName, true);
    pdf.addKeyValuePair('Position', jobTitle, true);
    pdf.addKeyValuePair('Company', company);
    pdf.addKeyValuePair('Date', new Date(analysis.created_at || Date.now()).toLocaleDateString());

    // Assessment Results
    pdf.addSection('Assessment Results');
    pdf.addScoreBar('Overall Fit Score', analysis.fit_score, 10);
    pdf.addKeyValuePair('Recommendation', getVerdictDisplay(analysis.verdict), true);

    // Key Findings
    pdf.addSection('Key Findings');
    pdf.addBulletPoints(analysis.reasoning || []);

    // Technical Skills
    pdf.addSection('Technical Skills');
    pdf.addTechnicalSkills(analysis.technical_skills || []);

    // Risk Assessment
    pdf.addSection('Risk Assessment');
    const riskFactors = analysis.risk_factors?.map(factor => parseRiskFactor(factor)).filter((factor): factor is RiskFactor => factor !== null) || [];
    pdf.addRiskFactors(riskFactors);

    // Growth Potential
    if (analysis.growth_potential) {
      pdf.addSection('Growth Potential');
      pdf.addGrowthPotential(analysis.growth_potential);
    }

    // Interview Plan
    if (transformedSections && transformedSections.length > 0) {
      // Use transformed sections with perspective-specific questions
      const perspectiveLabels = {
        'hr': 'HR Manager',
        'manager': 'Direct Manager', 
        'senior-manager': 'Senior Manager',
        'c-level': 'C-Level Executive',
        'technical-lead': 'Technical Lead'
      };
      
      const perspectiveLabel = selectedPerspective ? 
        perspectiveLabels[selectedPerspective as keyof typeof perspectiveLabels] || 'Standard' : 
        'Standard';
      
      pdf.addSection(`Interview Plan (${perspectiveLabel} Perspective)`);
      pdf.addInterviewPlan(transformedSections);
    } else if (analysis.interview_plan) {
      // Fallback to original interview plan if no transformed sections
      pdf.addSection('Interview Recommendations');
      pdf.addLongText(analysis.interview_plan);
    }

    // Footer and save
    pdf.addFooter(organization?.name);

    const date = new Date().toISOString().split('T')[0];
    const sanitizedCandidate = candidateName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const sanitizedJob = jobTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const filename = `analysis_${sanitizedCandidate}_${sanitizedJob}_${date}.pdf`;

    pdf.save(filename);

  } catch (error) {
    console.error('Failed to export analysis to PDF:', error);
    throw new Error('Failed to generate PDF report. Please try again.');
  }
}

export async function exportJobDescriptionToPDF(jobDescription: any) {
  try {
    const organization = await getUserOrganization();
    const pdf = new ProfessionalPDF();

    pdf.addHeader('Job Description', organization?.name);

    // Job Title and Company Overview
    pdf.addSection('Position Overview');
    if (jobDescription.jobTitle) pdf.addKeyValuePair('Job Title', jobDescription.jobTitle, true);
    if (jobDescription.company) pdf.addKeyValuePair('Company', jobDescription.company, true);
    if (jobDescription.location) pdf.addKeyValuePair('Location', jobDescription.location);
    if (jobDescription.salary) pdf.addKeyValuePair('Salary', jobDescription.salary);

    // Company Overview (if available from AI-generated content)
    if (jobDescription.companyOverview) {
      pdf.addSection('Company Overview');
      pdf.addLongText(jobDescription.companyOverview);
    }

    // Job Description (from AI-generated content or fallback)
    if (jobDescription.description || jobDescription.jobDescription) {
      pdf.addSection('Job Description');
      pdf.addLongText(jobDescription.description || jobDescription.jobDescription);
    }

    // Key Responsibilities (enhanced to handle both list and text formats)
    if (jobDescription.responsibilities) {
      pdf.addSection('Key Responsibilities');
      
      if (Array.isArray(jobDescription.responsibilities)) {
        pdf.addBulletPoints(jobDescription.responsibilities);
      } else if (typeof jobDescription.responsibilities === 'string') {
        // Handle both bulleted and paragraph format
        const responsibilities = jobDescription.responsibilities
          .split('\n')
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0)
          .map((line: string) => line.replace(/^[-•]\s*/, '').trim()); // Remove leading dashes and bullets
        
        // If it looks like bullet points, display as bullets
        if (responsibilities.some((item: string) => item.length < 100)) {
          pdf.addBulletPoints(responsibilities);
        } else {
          // If it's long form content, display as paragraphs
          pdf.addLongText(jobDescription.responsibilities);
        }
      }
    }

    // Required Skills & Technologies (enhanced parsing)
    if (jobDescription.requiredSkills || jobDescription.skills) {
      pdf.addSection('Required Skills & Technologies');
      const skills = (jobDescription.requiredSkills || jobDescription.skills);
      
      if (Array.isArray(skills)) {
        pdf.addBulletPoints(skills);
      } else if (typeof skills === 'string') {
        // Check if it's comma-separated or line-separated
        let skillsList: string[];
        
        if (skills.includes(',') && !skills.includes('\n')) {
          // Comma-separated format
          skillsList = skills.split(',').map(s => s.trim()).filter(s => s.length > 0);
        } else {
          // Line-separated format
          skillsList = skills.split('\n').map(s => s.trim().replace(/^[-•]\s*/, '')).filter(s => s.length > 0);
        }
        
        if (skillsList.length > 1) {
          pdf.addBulletPoints(skillsList);
        } else {
          // Display as text if only one item or unstructured
          pdf.addLongText(skills);
        }
      }
    }

    // Requirements/Qualifications (enhanced)
    if (jobDescription.requirements || jobDescription.qualifications) {
      pdf.addSection('Qualifications');
      const requirements = (jobDescription.requirements || jobDescription.qualifications);
      
      if (Array.isArray(requirements)) {
        pdf.addBulletPoints(requirements);
      } else if (typeof requirements === 'string') {
        const reqList = requirements
          .split('\n')
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0)
          .map((line: string) => line.replace(/^[-•]\s*/, '').trim());
        
        if (reqList.length > 1 && reqList.some((item: string) => item.length < 150)) {
          pdf.addBulletPoints(reqList);
        } else {
          pdf.addLongText(requirements);
        }
      }
    }

    // Benefits & Perks (enhanced)
    if (jobDescription.benefits) {
      pdf.addSection('Benefits & Perks');
      
      if (Array.isArray(jobDescription.benefits)) {
        pdf.addBulletPoints(jobDescription.benefits);
      } else if (typeof jobDescription.benefits === 'string') {
        const benefits = jobDescription.benefits
          .split('\n')
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0)
          .map((line: string) => line.replace(/^[-•]\s*/, '').trim());
        
        if (benefits.length > 1 && benefits.some((item: string) => item.length < 100)) {
          pdf.addBulletPoints(benefits);
        } else {
          pdf.addLongText(jobDescription.benefits);
        }
      }
    }

    // Work Environment (from AI-generated content)
    if (jobDescription.workEnvironment) {
      pdf.addSection('Work Environment');
      pdf.addLongText(jobDescription.workEnvironment);
    }

    // Equal Employment Opportunity (from AI-generated content)
    if (jobDescription.equalOpportunity) {
      pdf.addSection('Equal Employment Opportunity');
      pdf.addLongText(jobDescription.equalOpportunity);
    }

    // Company Mission/Values (if available)
    if (jobDescription.companyMission) {
      pdf.addSection('Company Mission & Values');
      pdf.addLongText(jobDescription.companyMission);
    }

    // If we have the full AI-generated description and nothing was parsed, show it all
    if (jobDescription.fullDescription && 
        !jobDescription.companyOverview && 
        !jobDescription.responsibilities && 
        !jobDescription.equalOpportunity) {
      pdf.addSection('Complete Job Description');
      pdf.addLongText(jobDescription.fullDescription);
    }

    pdf.addFooter(organization?.name);

    const filename = `job_description_${jobDescription.jobTitle?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'position'}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(filename);

  } catch (error) {
    console.error('Failed to export job description to PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
}

export async function exportSkillsAssessmentToPDF(assessment: any) {
  try {
    const organization = await getUserOrganization();
    const pdf = new ProfessionalPDF();

    // Header
    pdf.addHeader('Skills Assessment', organization?.name);

    // Assessment metadata
    pdf.addSection('Assessment Details');
    pdf.addKeyValuePair('Name', assessment.title || assessment.name || 'Custom Assessment', true);
    pdf.addKeyValuePair('Type', assessment.type || 'Custom');
    pdf.addKeyValuePair('Duration', `${assessment.duration || 60} minutes`);
    pdf.addKeyValuePair('Difficulty', assessment.difficulty || 'Intermediate');
    
    if (assessment.skills?.length > 0) {
      pdf.addKeyValuePair('Skills Covered', assessment.skills.join(', '));
    }

    // Instructions for candidates
    pdf.addSection('Instructions for Candidates');
    const candidateInstructions = [
      '• Read each question carefully before answering',
      '• Choose the best answer for multiple choice questions',
      '• Provide clear, concise responses for written questions',
      '• For coding questions, write clean, well-commented code',
      `• You have ${assessment.duration || 60} minutes to complete this assessment`,
      '• Submit your completed assessment when finished'
    ];
    
    pdf.addBulletPoints(candidateInstructions);

    // Questions section (without answers)
    if (assessment.questions?.length > 0) {
      pdf.addSection('Questions');
      assessment.questions.forEach((question: any, index: number) => {
        pdf.addQuestionWithOptions(question, index, false); // Don't show answers to candidates
      });
    }

    // Add Answer Key section for assessors
    if (assessment.questions?.length > 0) {
      pdf.addAnswerKey(assessment.questions);
    }

    pdf.addFooter(organization?.name);

    const filename = `skills_assessment_${(assessment.title || assessment.name || 'custom')?.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(filename);

  } catch (error) {
    console.error('Failed to export skills assessment to PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
}